/// <amd-module name="Release/Helpers/DateTime" />

import * as Resources from "Release/Helpers/Resources";

import { ago } from "VSSUI/Utilities/Date";
import { localeFormat } from "VSS/Core/Util/String";

export interface IDuration {
    days: number;
    hours: number;
    minutes: number;
    seconds: number;
    milliseconds: number;
}

export function padZeroes(number: number, times: number = 2): string {
    const localeTemplateString = "{0}{1}";
    let result = localeFormat(localeTemplateString, number, "");
    while (result.length < times) {
        result = localeFormat(localeTemplateString, 0, result);
    }

    return result;
}

export function diffDate(endDate: Date, startDate: Date): string {
    if (!endDate) {
        endDate = new Date();
    }
    if (!startDate) {
        startDate = new Date();
    }

    let friendlyString = "";
    let diffSeconds: number = Math.round(Math.abs(endDate.valueOf() - startDate.valueOf()) / 1000);

    const hours: number = Math.floor(diffSeconds / (60 * 60));
    friendlyString += padZeroes(hours, 2) + ":";
    diffSeconds = diffSeconds - hours * (60 * 60);

    const minutes = Math.floor(diffSeconds / 60);
    friendlyString += padZeroes(minutes, 2) + ":";
    diffSeconds = diffSeconds - minutes * 60;

    friendlyString += padZeroes(Math.round(diffSeconds), 2);

    return friendlyString;
}

export function parseDuration(duration: string): string {
    let durationString: string,
        durationObj: IDuration;

    const localeTemplateString = "{0} {1}";

    if (!duration) {
        return "";
    }

    durationObj = _getDurationObject(duration);

    if (durationObj.days > 0) {
        durationString = localeFormat(localeTemplateString,
            localeFormat(Resources.DayFormat, durationObj.days),
            (durationObj.hours > 0) ? localeFormat(Resources.HourFormat, durationObj.hours) : ""
        );
    } else if (durationObj.hours > 0) {
        durationString = localeFormat(localeTemplateString,
            localeFormat(Resources.HourFormat, durationObj.hours),
            (durationObj.minutes > 0) ? localeFormat(Resources.MinuteFormat, durationObj.minutes) : ""
        );
    } else if (durationObj.minutes > 0) {
        durationString = localeFormat(localeTemplateString,
            localeFormat(Resources.MinuteFormat, durationObj.minutes),
            (durationObj.seconds > 0) ? localeFormat(Resources.SecondFormat, (durationObj.seconds === Math.round(durationObj.seconds)) ? durationObj.seconds.toString() : durationObj.seconds.toFixed(2)) : ""
        );
    } else if (durationObj.seconds > 0) {
        durationString = localeFormat(localeTemplateString,
            localeFormat(Resources.SecondFormat, durationObj.seconds),
            (durationObj.milliseconds > 0) ? localeFormat(Resources.MillisecondFormat, durationObj.milliseconds) : ""
        );
    } else {
        if (durationObj.milliseconds === 0) {
            // Test took less than a ms. Report this as 0s
            durationString = localeFormat(Resources.SecondFormat, 0);
        } else {
            durationString = localeFormat(Resources.MillisecondFormat, durationObj.milliseconds);
        }
    }

    return durationString;
}

function _getDurationObject(duration: string): IDuration {
    // Check if we have days in the duration.
    let durationParts = duration.split(".");

    let days = 0;
    if (durationParts[0] && durationParts[1] && durationParts[1].indexOf(":") > 0) {
        // This means we have days in the duration. The duration is something like 2.03:04:50 or 1.04:50:47.6700 
        days = parseInt(durationParts[0]);
        duration = durationParts[1];
    }

    duration = duration.replace(".", ":");
    durationParts = duration.split(":");

    let milliSeconds = 0;
    if (durationParts[3]) {
        let milliSecondsAsSeconds = parseFloat("." + durationParts[3]);
        milliSeconds = Math.round(milliSecondsAsSeconds * 1000);
    }

    return {
        days: days,
        hours: (durationParts[0]) ? parseInt(durationParts[0]) : 0,
        minutes: (durationParts[1]) ? parseInt(durationParts[1]) : 0,
        seconds: (durationParts[2]) ? parseInt(durationParts[2]) : 0,
        milliseconds: milliSeconds
    }
}